import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Calculator, Calendar, Type, Link2 } from "lucide-react";
import { toast } from "sonner";

interface FormulaEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (formula: string, formulaType: string) => void;
  initialFormula?: string;
  initialType?: string;
}

export const FormulaEditor = ({ open, onClose, onSave, initialFormula = "", initialType = "text" }: FormulaEditorProps) => {
  const [formula, setFormula] = useState(initialFormula);
  const [nlPrompt, setNlPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const functionCategories = {
    numeric: [
      { name: "SUM(field1, field2)", desc: "Sum all values", example: "SUM({price}, {tax})" },
      { name: "AVG(field1, field2)", desc: "Average of values", example: "AVG({score1}, {score2})" },
      { name: "MIN(field1, field2)", desc: "Minimum value", example: "MIN({bid1}, {bid2})" },
      { name: "MAX(field1, field2)", desc: "Maximum value", example: "MAX({offer1}, {offer2})" },
      { name: "COUNT(field)", desc: "Count non-empty", example: "COUNT({items})" },
    ],
    text: [
      { name: "CONCAT(text1, text2)", desc: "Combine text", example: 'CONCAT({first_name}, " ", {last_name})' },
      { name: "UPPER(text)", desc: "Convert to uppercase", example: "UPPER({name})" },
      { name: "LOWER(text)", desc: "Convert to lowercase", example: "LOWER({email})" },
      { name: "TRIM(text)", desc: "Remove whitespace", example: "TRIM({input})" },
      { name: "LEFT(text, count)", desc: "Left characters", example: "LEFT({code}, 3)" },
      { name: "RIGHT(text, count)", desc: "Right characters", example: "RIGHT({phone}, 4)" },
      { name: "MID(text, start, count)", desc: "Extract substring", example: "MID({code}, 2, 4)" },
      { name: "LEN(text)", desc: "Character count", example: "LEN({description})" },
    ],
    date: [
      { name: "NOW()", desc: "Current date/time", example: "NOW()" },
      { name: "TODAY()", desc: "Current date", example: "TODAY()" },
      { name: "DATE_DIFF(date1, date2)", desc: "Days between dates", example: "DATE_DIFF(NOW(), {created_at})" },
      { name: "DATEADD(date, amount, unit)", desc: "Add to date", example: 'DATEADD({due_date}, 7, "days")' },
      { name: "YEAR(date)", desc: "Extract year", example: "YEAR({birthday})" },
      { name: "MONTH(date)", desc: "Extract month", example: "MONTH({created_at})" },
      { name: "DAY(date)", desc: "Extract day", example: "DAY({deadline})" },
    ],
    linked: [
      { name: "LOOKUP(linked, field)", desc: "Get linked value", example: "LOOKUP({company}, name)" },
      { name: "ROLLUP(linked, field, agg)", desc: "Aggregate linked", example: "ROLLUP({orders}, total, SUM)" },
      { name: "COUNTLINKS(linked)", desc: "Count links", example: "COUNTLINKS({tasks})" },
    ],
    logic: [
      { name: "IF(cond, true, false)", desc: "Conditional", example: 'IF({status} = "done", 100, 0)' },
    ],
  };

  const handleGenerateFromNL = async () => {
    if (!nlPrompt.trim()) {
      toast.error("Please describe what you want the formula to do");
      return;
    }

    setIsGenerating(true);
    // Simulate AI generation - in production, call an edge function
    setTimeout(() => {
      const examples = [
        "SUM({price}, {tax})",
        'IF({status} = "complete", 100, 0)',
        "DATE_DIFF(NOW(), {created_at})",
        "ROLLUP({line_items}, amount, SUM)",
        'CONCAT({first_name}, " ", {last_name})',
      ];
      setFormula(examples[Math.floor(Math.random() * examples.length)]);
      setIsGenerating(false);
      toast.success("Formula generated! Review and edit as needed.");
    }, 1500);
  };

  const handleSave = () => {
    if (!formula.trim()) {
      toast.error("Formula cannot be empty");
      return;
    }
    onSave(formula, initialType);
    onClose();
  };

  const insertFunction = (example: string) => {
    setFormula((prev) => prev + (prev ? " " : "") + example);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Formula Editor
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* AI Generation */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Natural Language to Formula
              </CardTitle>
              <CardDescription>Describe what you want in plain English</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              <Textarea
                value={nlPrompt}
                onChange={(e) => setNlPrompt(e.target.value)}
                placeholder="E.g., 'Calculate days since created' or 'Sum all linked order amounts'"
                rows={2}
              />
              <Button onClick={handleGenerateFromNL} disabled={isGenerating} size="sm">
                {isGenerating ? "Generating..." : "Generate Formula"}
              </Button>
            </CardContent>
          </Card>

          {/* Formula Input */}
          <div>
            <Label>Formula Expression</Label>
            <Textarea
              value={formula}
              onChange={(e) => setFormula(e.target.value)}
              placeholder="Enter formula, e.g., SUM({field1}) + {field2}"
              rows={3}
              className="font-mono text-sm mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Reference fields using {"{field_name}"}. Click functions below to insert.
            </p>
          </div>

          {/* Function Reference */}
          <Tabs defaultValue="numeric" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="numeric" className="gap-1 text-xs">
                <Calculator className="h-3 w-3" />
                Numeric
              </TabsTrigger>
              <TabsTrigger value="text" className="gap-1 text-xs">
                <Type className="h-3 w-3" />
                Text
              </TabsTrigger>
              <TabsTrigger value="date" className="gap-1 text-xs">
                <Calendar className="h-3 w-3" />
                Date
              </TabsTrigger>
              <TabsTrigger value="linked" className="gap-1 text-xs">
                <Link2 className="h-3 w-3" />
                Linked
              </TabsTrigger>
              <TabsTrigger value="logic" className="gap-1 text-xs">
                Logic
              </TabsTrigger>
            </TabsList>

            {Object.entries(functionCategories).map(([category, functions]) => (
              <TabsContent key={category} value={category} className="mt-2">
                <div className="grid grid-cols-2 gap-2">
                  {functions.map((func) => (
                    <div
                      key={func.name}
                      className="p-2 rounded-lg border hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => insertFunction(func.example)}
                    >
                      <code className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                        {func.name}
                      </code>
                      <p className="text-xs text-muted-foreground mt-1">{func.desc}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>

          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Formula</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
